import React from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Stethoscope, Users, Award, Clock, CheckCircle2, Phone, Mail, MapPin, ArrowRight, Shield, Briefcase } from 'lucide-react';

const Home = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Top Info Bar */}
      <div className="bg-gradient-to-r from-pink-600 to-sky-500 text-white py-2 px-6">
        <div className="container mx-auto flex flex-col sm:flex-row justify-between items-center text-sm gap-2">
          <div className="flex gap-6">
            <div className="flex items-center gap-2">
              <Phone className="w-4 h-4" />
              <span>888.764.3813</span>
            </div>
            <div className="flex items-center gap-2">
              <Mail className="w-4 h-4" />
              <span>info@medicalspecialistsfl.online</span>
            </div>
          </div>
          <div className="flex gap-4">
            <span className="text-pink-100">Mon-Fri: 9am-5pm | Sat-Sun: 11am-4pm</span>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="bg-white shadow-md sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-pink-500 to-sky-500 rounded-lg flex items-center justify-center">
                <Stethoscope className="w-7 h-7 text-white" />
              </div>
              <div>
                <div className="text-xl font-bold text-pink-600">medicalspecialistsfl.online</div>
                <div className="text-xs text-sky-600">Medical Specialists of Florida</div>
              </div>
            </div>
            <div className="hidden md:flex gap-8 items-center">
              <a href="#about" className="text-gray-700 hover:text-pink-600 font-medium transition-colors">About</a>
              <a href="#services" className="text-gray-700 hover:text-pink-600 font-medium transition-colors">Services</a>
              <a href="#why-us" className="text-gray-700 hover:text-pink-600 font-medium transition-colors">Why Choose Us</a>
              <a href="#contact" className="text-gray-700 hover:text-pink-600 font-medium transition-colors">Contact</a>
              <Button className="bg-gradient-to-r from-pink-500 to-sky-500 hover:from-pink-600 hover:to-sky-600">Request Services</Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-pink-50 via-white to-sky-50 py-20 px-6">
        <div className="container mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <Badge className="bg-pink-100 text-pink-700 border-pink-200">Medical Specialists of Florida</Badge>
              <h1 className="text-5xl md:text-6xl font-bold text-gray-900 leading-tight">
                Expert Medical Specialists
                <span className="block bg-gradient-to-r from-pink-600 to-sky-600 bg-clip-text text-transparent">When You Need Them Most</span>
              </h1>
              <p className="text-xl text-gray-600 leading-relaxed">
                Providing highly trained, certified medical specialists to healthcare facilities across Florida and Wyoming. Ensuring exceptional patient outcomes through professional medical support.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button size="lg" className="bg-gradient-to-r from-pink-500 to-sky-500 hover:from-pink-600 hover:to-sky-600">
                  Schedule Consultation <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
                <Button size="lg" variant="outline" className="border-pink-500 text-pink-600 hover:bg-pink-50">
                  View Our Services
                </Button>
              </div>
              <div className="flex gap-8 pt-4">
                <div>
                  <div className="text-3xl font-bold text-pink-600">1000+</div>
                  <div className="text-sm text-gray-600">Patients Served</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-sky-600">25+</div>
                  <div className="text-sm text-gray-600">Medical Specialists</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-pink-600">24/7</div>
                  <div className="text-sm text-gray-600">Support Available</div>
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-pink-300 to-sky-300 rounded-2xl blur-3xl opacity-20"></div>
              <img 
                src="https://images.unsplash.com/photo-1758206523735-079e56f2faf7?w=800&q=80&auto=format&fit=crop"
                alt="Surgical Team"
                loading="lazy"
                className="relative rounded-2xl shadow-2xl w-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 px-6 bg-white">
        <div className="container mx-auto">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <img 
                src="https://images.unsplash.com/photo-1760600865625-dbf6a3acfb67?w=700&q=80&auto=format&fit=crop"
                alt="Operating Room"
                loading="lazy"
                className="rounded-2xl shadow-xl"
              />
            </div>
            <div className="space-y-6">
              <Badge className="bg-sky-100 text-sky-700">About Us</Badge>
              <h2 className="text-4xl font-bold text-gray-900">
                Leading Provider of Medical Specialist Services
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                Medical Specialists of Florida is dedicated to providing healthcare facilities with highly trained, certified medical specialists. Our team of professionals brings years of experience and expertise to every case across our offices in Florida and Wyoming.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                We understand the critical importance of skilled medical support in achieving optimal patient outcomes. Our specialists are rigorously trained, continuously educated, and committed to maintaining the highest standards of care.
              </p>
              <div className="space-y-3">
                {[
                  'Board Certified Medical Specialists',
                  'Continuous Professional Development',
                  'Comprehensive Liability Insurance',
                  'Available During Business Hours & Emergency Support'
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <div className="w-6 h-6 bg-gradient-to-br from-pink-500 to-sky-500 rounded-full flex items-center justify-center flex-shrink-0">
                      <CheckCircle2 className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-gray-700 font-medium">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 px-6 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <Badge className="bg-pink-100 text-pink-700 mb-4">Our Services</Badge>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Comprehensive Medical Specialist Services
            </h2>
            <p className="text-lg text-gray-600">
              We provide expert medical specialists across multiple specialties, ensuring the highest quality support for healthcare providers.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                title: 'General Surgery',
                description: 'Experienced medical specialists for general surgical procedures including laparoscopic and open surgeries.',
                image: 'https://images.unsplash.com/photo-1758206523660-3ef5a51f1113?w=600&q=75&auto=format&fit=crop'
              },
              {
                title: 'Orthopedic Surgery',
                description: 'Specialized medical support for orthopedic procedures including joint replacements and trauma surgeries.',
                image: 'https://images.unsplash.com/photo-1758206523711-f20bb01033a5?w=600&q=75&auto=format&fit=crop'
              },
              {
                title: 'Cardiovascular Surgery',
                description: 'Highly trained specialists for complex cardiac and vascular surgical procedures.',
                image: 'https://images.unsplash.com/photo-1504439468489-c8920d796a29?w=600&q=75&auto=format&fit=crop'
              },
              {
                title: 'Neurosurgery',
                description: 'Precision medical support for delicate neurological and spinal surgical procedures.',
                image: 'https://images.unsplash.com/photo-1640876777002-badf6aee5bcc?w=600&q=75&auto=format&fit=crop'
              },
              {
                title: 'Plastic & Reconstructive',
                description: 'Skilled medical assistance for cosmetic and reconstructive surgical procedures.',
                image: 'https://images.unsplash.com/photo-1685997180450-242a65624238?w=600&q=75&auto=format&fit=crop'
              },
              {
                title: 'Emergency Medicine',
                description: 'Support during business hours with emergency consultation available.',
                image: 'https://images.unsplash.com/photo-1744829903372-6e6f254780bb?w=600&q=75&auto=format&fit=crop'
              }
            ].map((service, index) => (
              <Card key={index} className="overflow-hidden hover:shadow-xl transition-shadow group">
                <div className="relative h-48 overflow-hidden">
                  <img 
                    src={service.image}
                    alt={service.title}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                  <h3 className="absolute bottom-4 left-4 text-white font-bold text-xl">{service.title}</h3>
                </div>
                <CardContent className="pt-6">
                  <p className="text-gray-600 leading-relaxed">{service.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section id="why-us" className="py-20 px-6 bg-gradient-to-br from-pink-500 to-sky-500 text-white">
        <div className="container mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">Why Choose Us</h2>
            <p className="text-xl text-pink-50">
              We set the standard for excellence in medical specialist services across Florida and Wyoming.
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            {[
              {
                icon: <Award className="w-12 h-12" />,
                title: 'Certified Professionals',
                description: 'All our medical specialists are board-certified and highly experienced.'
              },
              {
                icon: <Shield className="w-12 h-12" />,
                title: 'Fully Insured',
                description: 'Comprehensive liability coverage for your peace of mind.'
              },
              {
                icon: <Clock className="w-12 h-12" />,
                title: 'Regular Hours',
                description: 'Mon-Fri: 9am-5pm, Sat-Sun: 11am-4pm with emergency support available.'
              },
              {
                icon: <Briefcase className="w-12 h-12" />,
                title: 'Proven Track Record',
                description: 'Years of experience supporting successful patient outcomes.'
              }
            ].map((item, index) => (
              <div key={index} className="text-center">
                <div className="w-20 h-20 bg-white/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  {item.icon}
                </div>
                <h3 className="text-xl font-bold mb-2">{item.title}</h3>
                <p className="text-pink-50">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Statistics */}
      <section className="py-20 px-6 bg-gray-50">
        <div className="container mx-auto">
          <div className="grid md:grid-cols-4 gap-8 text-center">
            {[
              { number: '1000+', label: 'Patients Served Annually' },
              { number: '25+', label: 'Certified Medical Specialists' },
              { number: '2', label: 'Office Locations (FL & WY)' },
              { number: '99.5%', label: 'Client Satisfaction Rate' }
            ].map((stat, index) => (
              <div key={index}>
                <div className="text-5xl font-bold bg-gradient-to-r from-pink-600 to-sky-600 bg-clip-text text-transparent mb-2">{stat.number}</div>
                <div className="text-gray-600">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section id="contact" className="py-20 px-6 bg-white">
        <div className="container mx-auto">
          <div className="max-w-4xl mx-auto bg-gradient-to-br from-pink-50 to-sky-50 rounded-3xl p-12 text-center border-2 border-pink-200">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Ready to Experience Expert Medical Care?
            </h2>
            <p className="text-xl text-gray-600 mb-8">
              Contact us today to discuss your medical specialist needs. We're here to support your success.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              <Button size="lg" className="bg-gradient-to-r from-pink-500 to-sky-500 hover:from-pink-600 hover:to-sky-600">
                Request Consultation
              </Button>
              <Button size="lg" variant="outline" className="border-pink-500 text-pink-600 hover:bg-pink-50">
                Call 888.764.3813
              </Button>
            </div>
            <div className="grid md:grid-cols-3 gap-6 mt-12">
              {[
                { icon: <Phone className="w-6 h-6" />, title: 'Phone', info: '888.764.3813' },
                { icon: <Mail className="w-6 h-6" />, title: 'Email', info: 'info@medicalspecialistsfl.online' },
                { icon: <MapPin className="w-6 h-6" />, title: 'Main Office', info: '1515 S Federal Hwy Ste 406, Boca Raton, FL 33432' }
              ].map((contact, index) => (
                <div key={index} className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-pink-500 to-sky-500 rounded-lg flex items-center justify-center text-white flex-shrink-0">
                    {contact.icon}
                  </div>
                  <div className="text-left">
                    <div className="font-bold text-gray-900">{contact.title}</div>
                    <div className="text-sm text-gray-600">{contact.info}</div>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-8 text-center">
              <p className="text-sm text-gray-600 font-medium">Headquarters: 419 S Washington St Ste 200, Casper, WY 82601</p>
              <p className="text-sm text-gray-500 mt-2">Mon-Fri: 9am-5pm | Sat-Sun: 11am-4pm</p>
              <p className="text-sm text-gray-600 mt-3">
                NPI: <a 
                  href="https://npiregistry.cms.hhs.gov/provider-view/1457263972" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-pink-600 hover:text-pink-700 underline font-medium transition-colors"
                >
                  1457263972
                </a>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 px-6">
        <div className="container mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-pink-500 to-sky-500 rounded-lg flex items-center justify-center">
                  <Stethoscope className="w-6 h-6" />
                </div>
                <span className="text-xl font-bold">Medical Specialists of Florida</span>
              </div>
              <p className="text-gray-400 mb-2">
                Professional medical specialist services you can trust.
              </p>
              <p className="text-gray-400 text-sm">
                medicalspecialistsfl.online
              </p>
            </div>
            <div>
              <h4 className="font-bold mb-4">Services</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#" className="hover:text-white transition-colors">General Surgery</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Orthopedic Surgery</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Cardiovascular</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Emergency Support</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-4">Company</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#" className="hover:text-white transition-colors">About Us</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Our Team</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Contact</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Locations</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-4">Contact</h4>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li>Phone: 888.764.3813</li>
                <li>Email: info@medicalspecialistsfl.online</li>
                <li className="pt-2">
                  NPI: <a 
                    href="https://npiregistry.cms.hhs.gov/provider-view/1457263972" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-pink-400 hover:text-pink-300 underline transition-colors"
                  >
                    1457263972
                  </a>
                </li>
                <li className="pt-2 border-t border-gray-800">Main Office:<br/>1515 S Federal Hwy Ste 406<br/>Boca Raton, FL 33432</li>
                <li className="pt-2">Headquarters:<br/>419 S Washington St Ste 200<br/>Casper, WY 82601</li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 text-center text-gray-400">
            <p>&copy; 2024 Medical Specialists of Florida. All rights reserved.</p>
            <p className="text-sm mt-2">medicalspecialistsfl.online | 888.764.3813</p>
            <p className="text-sm mt-2">
              NPI: <a 
                href="https://npiregistry.cms.hhs.gov/provider-view/1457263972" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-pink-400 hover:text-pink-300 underline transition-colors"
              >
                1457263972
              </a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;